"use client";

import { useLayoutEffect, useRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

interface AvatarGridProps extends HTMLAttributes<HTMLDivElement> {
  size?: "sm" | "default";
}

/** Fixed-size avatars slide to their new positions when the grid reflows. */
export function AvatarGrid({
  className,
  size = "default",
  ...props
}: AvatarGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid || typeof ResizeObserver === "undefined") return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const positions = new Map<HTMLElement, { x: number; y: number }>();
    const animations = new Map<HTMLElement, Animation>();

    const updatePositions = () => {
      // Read layout before writing animations. Offsets exclude both our
      // translation and the buttons' independent hover-scale transform.
      const items = Array.from(grid.children)
        .filter(
          (child): child is HTMLButtonElement =>
            child instanceof HTMLButtonElement,
        )
        .map((element) => {
          const previous = positions.get(element);
          const x = element.offsetLeft;
          const y = element.offsetTop;
          // Continue from the visible position when a resize interrupts a slide.
          const translation = animations.has(element)
            ? (getComputedStyle(element).translate || "none")
                .split(" ")
                .map(parseFloat)
            : [];
          return {
            element,
            x,
            y,
            dx: previous ? previous.x - x + (translation[0] || 0) : 0,
            dy: previous ? previous.y - y + (translation[1] || 0) : 0,
          };
        });

      for (const { element, x, y, dx, dy } of items) {
        const previous = positions.get(element);
        positions.set(element, { x, y });
        if (!reducedMotion.matches && previous?.x === x && previous?.y === y)
          continue;

        animations.get(element)?.cancel();
        animations.delete(element);
        if (reducedMotion.matches || !element.animate || (dx === 0 && dy === 0))
          continue;

        // Animate translation only: avatar dimensions and hover effects stay intact.
        const animation = element.animate(
          [{ translate: `${dx}px ${dy}px` }, { translate: "0px 0px" }],
          { duration: 240, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
        );
        animations.set(element, animation);
        animation.onfinish = () => {
          if (animations.get(element) === animation) animations.delete(element);
        };
      }
    };

    updatePositions();
    const observer = new ResizeObserver(updatePositions);
    observer.observe(grid);
    reducedMotion.addEventListener("change", updatePositions);
    return () => {
      observer.disconnect();
      reducedMotion.removeEventListener("change", updatePositions);
      animations.forEach((animation) => animation.cancel());
    };
  }, []);

  return (
    <div
      ref={gridRef}
      className={cn(
        "relative grid w-full min-w-0 justify-start gap-3",
        size === "sm"
          ? "grid-cols-[repeat(auto-fill,3rem)] [&>button]:size-12"
          : "grid-cols-[repeat(auto-fill,3.5rem)] [&>button]:size-14",
        className,
      )}
      {...props}
    />
  );
}
